import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["id", "onMousedown"]
const _hoisted_2 = ["innerHTML"]

import { type PropType, ref, nextTick } from 'vue';
import debouce from 'progressme-debounce';
/**
 * * Свойства
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'tir-drag',
  props: {
  /**
   * * ID элемента
   */
  id: { type: Number, default: -1 },
  /**
   * * Классы элемента и его копии
   */
  classes: { type: String, default: '' },
  /**
   * * Класс элемента, при перетаскивании его копии
   */
  dragClass: { type: String, default: '' },
  /**
   * * Класс клона, который перетаскиваем
   */
  grabbingClass: { type: String, default: '' },
  /**
   * * Класс элемента на который дропают текщий
   */
  droppableClass: { type: String, default: '' },
  /**
   * * Классы элементов на которые может дропаться текщий
   */
  droppableClasses: {
    type: Array as PropType<string[]>,
    default: ['droppable'],
  },
  /**
   * * Запретить перетаскивать
   */
  disabled: { type: Boolean, default: false },
  /**
   * * Задержка перед началом перетаскивания
   */
  delay: { type: Number, default: 0 },
},
  emits: ["start", "stop", "enter", "leave"],
  setup(__props, { expose: __expose, emit }) {

const props = __props;


/**
 * * Элемент
 */
const $el = ref<HTMLElement>();
/**
 * * Клон, который перетаскиваем
 */
const $grabbing = ref<HTMLElement>();
/**
 * * Позиция элемента
 */
const rect = ref<DOMRect>();

/**
 * * Перетаскивается ли элемент
 */
const isDrag = ref(false);
/**
 * * Начальные координаты
 */
const sx = ref<number>();
const sy = ref<number>();
/**
 * * Текущие координаты курсора
 */
const x = ref(0);
const y = ref(0);
/**
 * * События
 */

/**
 * * Элемент для дропа
 */
const droppable = ref<HTMLElement>();
/**
 * * Игнорировать
 */
const touchstartIgnore = ref(false);
const mousedownIgnore = ref(false);
function ignoreTouchstart() {
  window.removeEventListener('touchmove', touchmoveBeforeStart);
  touchstartIgnore.value = true;
}
function ignoreMousedown() {
  window.removeEventListener('mousemove', mousemoveBeforeStart);
  mousedownIgnore.value = true;
}
function onTouchstart(e: TouchEvent) {
  touchstartIgnore.value = false;
  window.addEventListener('touchend', ignoreTouchstart, { once: true });
  window.addEventListener('touchmove', touchmoveBeforeStart);
  delayTouchstart(e);
}
function onMousedown(e: MouseEvent) {
  mousedownIgnore.value = false;
  window.addEventListener('mouseup', ignoreMousedown, { once: true });
  window.addEventListener('mousemove', mousemoveBeforeStart);
  delayMousedown(e);
}
/**
 * * Вычислять позицию курсора до начала перетаскивания
 */
function mousemoveBeforeStart(e: MouseEvent) {
  sx.value = e.clientX;
  sy.value = e.clientY;
}
function touchmoveBeforeStart(e: TouchEvent) {
  sx.value = e.changedTouches[0].clientX;
  sy.value = e.changedTouches[0].clientY;
}
/**
 * * Перетаскивание мышью
 */
function mousedown(e: MouseEvent) {
  if (isDrag.value || props.disabled) return;

  if (mousedownIgnore.value) {
    mousedownIgnore.value = false;
    return;
  }

  start(e.clientX, e.clientY).then(() => {
    window.removeEventListener('mouseup', ignoreMousedown);
    window.removeEventListener('mousemove', mousemoveBeforeStart);
    window.addEventListener('mousemove', mousemove);
    window.addEventListener('mouseup', mouseup);
  });
}
const delayMousedown = debouce(mousedown, props.delay);
function mousemove(e: MouseEvent) {
  if (!isDrag.value) return;

  drag(e.clientX, e.clientY);
}
function mouseup(e: MouseEvent) {
  if (!isDrag.value) return;

  window.removeEventListener('mousemove', mousemove);
  window.removeEventListener('mouseup', mouseup);

  stop();
}
function touchstart(e: TouchEvent) {
  if (isDrag.value || props.disabled) return;

  if (touchstartIgnore.value) {
    touchstartIgnore.value = false;
    return;
  }

  start(e.changedTouches[0].clientX, e.changedTouches[0].clientY).then(() => {
    window.removeEventListener('touchend', ignoreTouchstart);
    window.removeEventListener('touchmove', touchmoveBeforeStart);
    window.addEventListener('touchmove', touchmove);
    window.addEventListener('touchend', touchend);
  });
}
const delayTouchstart = debouce(touchstart, props.delay);
function touchmove(e: TouchEvent) {
  if (!isDrag.value) return;

  drag(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
}
function touchend(e: TouchEvent) {
  if (!isDrag.value) return;

  window.removeEventListener('touchmove', touchmove);
  window.removeEventListener('touchend', touchend);

  stop();
}

/**
 * * Начало перетаскивания
 * @param cx - clientX
 * @param cy - clientY
 */
async function start(cx: number, cy: number) {
  if (isDrag.value || !$el.value) return;

  await nextTick();
  rect.value = $el.value.getBoundingClientRect();

  window.removeEventListener('mousemove', mousemoveBeforeStart);

  emit('start', $el.value, Number(props.id));

  x.value = sx.value == undefined ? cx : sx.value;
  y.value = sy.value == undefined ? cy : sy.value;

  sx.value = cx;
  sy.value = cy;

  isDrag.value = true;

  elementHtml.value = $el.value.innerHTML;
}
/**
 * * Перетаскивание
 * @param cx - clientX
 * @param cy - clientY
 */
function drag(cx: number, cy: number) {
  if (!isDrag.value || !$grabbing.value) return;

  x.value = cx;
  y.value = cy;

  if (y.value < 0 && window.scrollY > 0) {
    window.scrollTo(0, window.scrollY + y.value);
    y.value = 0;
  } else if (y.value > window.innerHeight) {
    window.scrollTo(0, window.scrollY + (y.value - window.innerHeight));
    y.value = window.innerHeight;
  }

  $grabbing.value.style.overflow = 'hidden';
  const el = document.elementFromPoint(x.value, y.value) as HTMLElement;
  $grabbing.value.style.overflow = 'visible';

  if (!el) return;

  let _droppable: HTMLElement | undefined;
  props.droppableClasses.forEach((cls) => {
    const _el = el.closest(`.${cls}`) as HTMLElement;
    if (_el) {
      _droppable = _el;
    }
  });
  if (_droppable && _droppable != $el.value) {
    if (_droppable != droppable.value) {
      if (droppable.value != _droppable && props.droppableClass)
        droppable.value?.classList.remove(props.droppableClass);
      droppable.value = _droppable;
      if (props.droppableClass) {
        droppable.value?.classList.add(props.droppableClass);
      }
      emit('enter', droppable.value, Number(droppable.value.id));
    }
  } else if (droppable.value) {
    emit('leave', el, Number((droppable.value as Element).id));
    if (props.droppableClass)
      droppable.value?.classList.remove(props.droppableClass);
    droppable.value = undefined;
  }
}
/**
 * * Остановка перетаскивания
 */
function stop() {
  if (!isDrag.value) return;

  emit('stop', droppable.value, Number((droppable.value as Element)?.id));

  sx.value = undefined;
  sy.value = undefined;

  x.value = 0;
  y.value = 0;

  if (props.droppableClass)
    droppable.value?.classList.remove(props.droppableClass);
  droppable.value = undefined;

  isDrag.value = false;
}
/**
 * * Html элемента
 */
const elementHtml = ref('');
/**
 * * Поделиться
 */
__expose({
  stop,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    class: _normalizeClass(["tir-drag", [__props.classes, { dragged: isDrag.value }, isDrag.value ? __props.dragClass : '']]),
    id: __props.id?.toString(),
    ref_key: "$el",
    ref: $el,
    onMousedown: _withModifiers(onMousedown, ["prevent"]),
    onTouchstart: onTouchstart
  }, [
    _renderSlot(_ctx.$slots, "default"),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isDrag.value)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            ref_key: "$grabbing",
            ref: $grabbing,
            class: "tir-drag_grabbing",
            style: _normalizeStyle({
          top: `calc(${y.value}px)`,
          left: `calc(${x.value}px)`,
        })
          }, [
            _createElementVNode("span", {
              class: _normalizeClass(["tir-drag", [__props.classes, __props.grabbingClass]]),
              style: _normalizeStyle({
            width: `${rect.value?.width}px`,
            height: `${rect.value?.height}px`,
            left: `calc(${rect.value?.left}px - ${sx.value || rect.value?.left}px)`,
            top: `calc(${rect.value?.top}px - ${sy.value || rect.value?.top}px)`,
          }),
              innerHTML: elementHtml.value
            }, null, 14, _hoisted_2)
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ], 42, _hoisted_1))
}
}

})